import {
  Component,
  ViewChild,
  ElementRef,
  Input,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  NgZone,
  AfterViewInit,
  HostListener
} from '@angular/core';
import { MatSidenav } from '@angular/material/sidenav';
import { MatDialog } from '@angular/material/dialog';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import * as showdown from 'showdown';
import { Subscription, firstValueFrom } from 'rxjs';

import { ChatFireService } from 'src/app/gen2ai/chatfire.service';
import { SideMenusService } from '../../core/side-menus/side-menus.service';
import { Conversation, Message } from '../../models/chat.model';
import { showdownMarkdownClassesExtension } from '../../shared/markdown/showdown-md-classes.extension';
import { aggressivelyFormatMarkdownForCodeBlocks } from '../../shared/markdown/aggressive-code-format';

import mkPacmanClient from 'pacman-client';

// dialogs (authentication/dialogs)
import { DeepseekComponent } from '../../authentication/dialogs/deepseek/deepseek.component';
import { OpusFourComponent } from '../../authentication/dialogs/opus-four/opus-four.component';
import { SonnetFourComponent } from '../../authentication/dialogs/sonnet-four/sonnet-four.component';
import { GptFourOneComponent } from '../../authentication/dialogs/gpt-four-one/gpt-four-one.component';
import { FourOMiniComponent } from '../../authentication/dialogs/four-o-mini/four-o-mini.component';
import { CohereRPlusComponent } from '../../authentication/dialogs/cohere-r-plus/cohere-r-plus.component';
import { OThreeComponent } from '../../authentication/dialogs/o-three/o-three.component';
import { LlamaThreePointThreeComponent } from '../../authentication/dialogs/llama-three-point-three/llama-three-point-three.component';
import { MistralLargeComponent } from '../../authentication/dialogs/mistral-large/mistral-large.component';
import { MaverickComponent } from '../../authentication/dialogs/maverick/maverick.component';
import { JambaComponent } from '../../authentication/dialogs/jamba/jamba.component';
import { PixtralImageComponent } from '../../authentication/dialogs/pixtral-image/pixtral-image.component';
import { GeminiProComponent } from '../../authentication/dialogs/gemini-pro/gemini-pro.component';
import { ScoutComponent } from '../../authentication/dialogs/scout/scout.component';
import { GptFiveComponent } from '../../authentication/dialogs/gpt-five/gpt-five.component';
import { DalEComponent } from '../../authentication/dialogs/dal-e/dal-e.component';
import { GrokImageComponent } from '../../authentication/dialogs/grok-image/grok-image.component';
import { StabilityComponent } from '../../authentication/dialogs/stability/stability.component';
import { HaikuComponent } from '../../authentication/dialogs/haiku/haiku.component';
import { PalmyraComponent } from '../../authentication/dialogs/palmyra/palmyra.component';
import { NovaLiteComponent } from '../../authentication/dialogs/nova-lite/nova-lite.component';
import { NovaProComponent } from '../../authentication/dialogs/nova-pro/nova-pro.component';

// NEW dialog
import { AiCatalogComponent } from '../../authentication/dialogs/ai-catalog/ai-catalog.component';

// NEW: service used to push images into live audio
import { AIFireService } from '../../gen2ai/fire.service';

// Voice dialog component
import { TheVoiceComponent } from '../../authentication/dialogs/the-voice/the-voice.component';

import { TourStep, TourService } from 'src/app/authentication/tour.service';

import { MainMenuComponent } from 'src/app/core/side-menus/main-menu/main-menu.component';
import { ReasoningService, ReasoningLevel } from 'src/app/services/reasoning.service';
import { AuthenticationService } from 'src/app/clari-auth/authentication.service';
import { ModelSelectionService } from 'src/app/services/model-selection.service';

import { NotesService } from '../../services/notes.service';

import { ModelSwitchOverlayService } from 'src/app/services/model-switch-overlay.service';
import { GlmComponent } from '../../authentication/dialogs/glm/glm.component';
import { Deepseekv3Component } from '../../authentication/dialogs/deepseekv3/deepseekv3.component';
import { GoogleDocsService } from '../../shared/google-docs-integration/google-docs.service';

import jsPDF from 'jspdf';
import * as docx from 'docx';
import { saveAs } from 'file-saver';

// Interface for placeholder options with paired text, logo, and name
interface PlaceholderOption {
  text: string;
  logoUrl: string;
  name: string;
}

@Component({
  selector: 'app-parallax-chat',
  templateUrl: './parallax-chat.component.html',
  styleUrls: ['./parallax-chat.component.scss']
})
export class ParallaxChatComponent implements OnInit, AfterViewInit, OnDestroy {
  form: FormGroup;
  o3form: FormGroup;

  prompt = '';
  selectedModel$: any;
  inputText = '';
  selectedFile?: File;
  selectedImagePreview: string | null = null;
  theSummary: string | null = null;
  openActionsMessageId: string | null = null;
  collapsedQaBlocks = new Set<string>();

  @Input() id!: string;

  currentMessages: Message[] = [];
  currentConversation?: Conversation;
  converter: showdown.Converter;
  convertedMarkdown = '';

  isLoading = false;
  userInput = '';
  private userScrolledUp = false;

/* ========================================================================== */
/*  Scroll rail (see below)      
/* ========================================================================== */
activeMessageIndex: number = 0;
private msgObserver?: IntersectionObserver;
private msgElements: HTMLElement[] = [];
private forceLastTick = false;
private forceLastTickTimer: any = null;

// Tour state tracking
isTourActive = false;
tourStepId: string | null = null;

@ViewChild('parallaxCurtain') parallaxCurtain?: ElementRef<HTMLElement>;
@ViewChild('parallaxMain') parallaxMain?: ElementRef<HTMLElement>;
curtainActive = false;
private curtainHeight = 0;
private mainHeight = 0;
private parallaxTicking = false;

private setupMessageObserver(): void {
  if (!this.messagesContainer?.nativeElement) return;

  // Clean up old observer if it exists
  this.msgObserver?.disconnect();

  const container = this.messagesContainer.nativeElement;

  // Capture all user messages
  this.msgElements = Array.from(
    container.querySelectorAll('.message.user-message')
  ) as HTMLElement[];

  if (!this.msgElements.length) return;

  this.msgObserver = new IntersectionObserver(
  entries => {

    //scroll rail
    if (this.forceLastTick) return;

    const candidates = entries.filter(e => e.isIntersecting);
    if (!candidates.length) return;

    const best = candidates.reduce((prev, curr) =>
      (curr.intersectionRatio ?? 0) > (prev.intersectionRatio ?? 0)
        ? curr
        : prev
    );

    const index = this.msgElements.indexOf(best.target as HTMLElement);

    if (index !== -1) {
      this.activeMessageIndex = index;
      this.cdr.detectChanges();
    }
  },
  {
    root: container,
    threshold: [0.2, 0.4, 0.6, 0.8],
    rootMargin: '-20% 0px -55% 0px'
  }
);

  this.msgElements.forEach(el => this.msgObserver?.observe(el));

  if (!this.userScrolledUp) {
    this.activeMessageIndex = this.msgElements.length - 1;
    this.cdr.detectChanges();
  }
}

get userMessages(): Message[] {
  return this.currentMessages.filter(m => m.role === 'user');
}

private forceHighlightLastTick(): void {
  this.forceLastTick = true;

  if (this.forceLastTickTimer) {
    clearTimeout(this.forceLastTickTimer);
    this.forceLastTickTimer = null;
  }

  setTimeout(() => {
    this.setupMessageObserver();

    this.activeMessageIndex = this.msgElements.length - 1;
    this.cdr.detectChanges();

    this.forceLastTickTimer = setTimeout(() => {
      this.forceLastTick = false;
    }, 800);
  }, 0);
}

private updateParallaxMetrics(): void {
  const curtainEl = this.parallaxCurtain?.nativeElement;
  if (!curtainEl) return;
  this.curtainHeight = curtainEl.offsetHeight || window.innerHeight;
  const mainEl = this.parallaxMain?.nativeElement;
  this.mainHeight = mainEl?.offsetHeight || window.innerHeight;
  this.hostRef.nativeElement.style.setProperty('--parallax-main-height', `${this.mainHeight}px`);
  this.hostRef.nativeElement.style.setProperty('--parallax-curtain-height', `${this.curtainHeight}px`);
}

private updateParallaxState(): void {
  const curtainEl = this.parallaxCurtain?.nativeElement;
  if (!curtainEl) return;

  const height = this.mainHeight || window.innerHeight;
  const scrollY = window.scrollY || window.pageYOffset || 0;
  const delta = scrollY - this.lastScrollY;
  if (Math.abs(delta) > 6) {
    const direction: 'up' | 'down' = delta > 0 ? 'down' : 'up';
    if (this.lastScrollDirection && direction !== this.lastScrollDirection) {
      this.triggerCurtainFlip();
    }
    this.lastScrollDirection = direction;
    this.lastScrollY = scrollY;
  }
  const progress = Math.min(Math.max(scrollY / height, 0), 1);
  const drift = Math.min(scrollY * 0.25, (this.curtainHeight || window.innerHeight) * 0.4);
  const reveal = Math.min(scrollY, height);

  this.curtainActive = progress > 0.08;
  this.hostRef.nativeElement.style.setProperty('--parallax-progress', progress.toFixed(3));
  this.hostRef.nativeElement.style.setProperty('--parallax-drift', `${drift.toFixed(2)}px`);
  this.hostRef.nativeElement.style.setProperty('--parallax-reveal', `${reveal.toFixed(2)}px`);
}

private triggerCurtainFlip(): void {
  this.curtainFlipActive = false;
  if (this.curtainFlipTimer) {
    clearTimeout(this.curtainFlipTimer);
  }
  this.cdr.detectChanges();
  this.curtainFlipTimer = setTimeout(() => {
    this.curtainFlipActive = true;
    this.cdr.detectChanges();
    this.curtainFlipTimer = setTimeout(() => {
      this.curtainFlipActive = false;
      this.cdr.detectChanges();
    }, 700);
  }, 0);
}

/* ========================================================================== */
/*  Scroll rail (see above)      
/* ========================================================================== */


/* ========================================================================== */
/*  Exporters (PDF & Doc) (see below)      
/* ========================================================================== */

private messageTitle(msg: Message): string {
  if (msg.role === 'user') return 'User Message';
  // assistant
  return msg.modelDisplayName ? `Assistant (${msg.modelDisplayName})` : 'Assistant Message';
}

private messageBody(msg: Message): string { //Markdown for exporters
  return this.stripMarkdownForExport(this.normalizeMessageContent(msg?.content));
}

exportMessageToPDF(msg: Message): void {
  this.openActionsMessageId = null;
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const margin = 40;
  const width = doc.internal.pageSize.getWidth() - margin * 2;
  let y = margin;
  const title = this.messageTitle(msg);
  const text = this.messageBody(msg);
  doc.setFont('Helvetica', 'bold');
  doc.setFontSize(14);
  doc.text(title, margin, y);
  y += 18;
  doc.setFont('Helvetica', 'normal');
  doc.setFontSize(12);

  const lines = doc.splitTextToSize(text, width);
  for (const line of lines) {
    if (y > doc.internal.pageSize.getHeight() - margin) {
      doc.addPage();
      y = margin;
    }
    doc.text(line, margin, y);
    y += 16;
  }

  doc.save('clarichat-message.pdf');
}

exportMessageToDOCX(msg: Message): void {
  this.openActionsMessageId = null;
  const title = this.messageTitle(msg);
  const body = this.messageBody(msg);
  const children: docx.Paragraph[] = [
    new docx.Paragraph({
      text: title,
      heading: docx.HeadingLevel.HEADING_2,
      spacing: { after: 240 }
    })
  ];

  body.split('\n').forEach(line => {
    children.push(new docx.Paragraph({ text: line.trim(), spacing: { after: 120 } }));
  });

  const d = new docx.Document({
    sections: [{ properties: {}, children }]
  });

  docx.Packer.toBlob(d).then(blob => saveAs(blob, 'clarichat-message.docx'));
}

async exportMessageToGoogleDocs(msg: Message): Promise<void> {
  const normalizedContent = this.normalizeMessageContent(msg?.content);
  if (!normalizedContent.trim()) return;
  if (msg.role !== 'assistant') return;

  const timestamp = msg.timestamp ? new Date(msg.timestamp as any).toLocaleString() : '';
  const modelName = msg.modelDisplayName || msg.modelId || 'Assistant';
  const titleBase = `ClariChat — ${modelName}`;
  const title = timestamp ? `${titleBase} (${timestamp})` : titleBase;
  const userPrompt = this.getPreviousUserPrompt(msg);

  try {
    const created = await this.googleDocsService.createDocFromAssistantResponse(
      title,
      normalizedContent,
      userPrompt
    );
    if (created?.url) {
      window.open(created.url, '_blank');
    }
    this.showActionToast(msg, 'Created Google Doc ✓');
  } catch (error) {
    console.error('Failed to create Google Doc:', error);
    this.showActionToast(msg, 'Google Doc creation failed', 'warn');
  } finally {
    this.openActionsMessageId = null;
  }
}

private getPreviousUserPrompt(msg: Message): string {
  const messages = this.currentMessages || [];
  let index = -1;
  if (msg.id) {
    index = messages.findIndex((entry) => entry.id === msg.id);
  }
  if (index === -1) {
    index = messages.indexOf(msg);
  }
  for (let i = index - 1; i >= 0; i -= 1) {
    if (messages[i]?.role === 'user') {
      return this.normalizeMessageContent(messages[i]?.content);
    }
  }
  return '';
}

exportConversationToPDF(): void {
  if (!this.currentMessages?.length) return;
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const margin = 40;
  const width = doc.internal.pageSize.getWidth() - margin * 2;
  let y = margin;

  const add = (title: string, text: string) => {
    doc.setFont('Helvetica', 'bold');
    doc.setFontSize(14);
    doc.text(title, margin, y);
    y += 18;

    doc.setFont('Helvetica', 'normal');
    doc.setFontSize(12);

    const lines = doc.splitTextToSize(text, width);
    for (const line of lines) {
      if (y > doc.internal.pageSize.getHeight() - margin) {
        doc.addPage();
        y = margin;
      }
      doc.text(line, margin, y);
      y += 16;
    }
    y += 8;
  };

  this.currentMessages.forEach(msg => add(this.messageTitle(msg), this.messageBody(msg)));

  doc.save('clarichat-conversation.pdf');
}

exportConversationToDOCX(): void {
  if (!this.currentMessages?.length) return;
  const children: docx.Paragraph[] = [];

  this.currentMessages.forEach(msg => {
    children.push(
      new docx.Paragraph({
        text: this.messageTitle(msg),
        heading: docx.HeadingLevel.HEADING_2,
        spacing: { after: 240 }
      })
    );

    this.messageBody(msg).split('\n').forEach(line => {
      children.push(new docx.Paragraph({ text: line.trim(), spacing: { after: 120 } }));
    });
  });

  const d = new docx.Document({
    sections: [{ properties: {}, children }]
  });

  docx.Packer.toBlob(d).then(blob => saveAs(blob, 'clarichat-conversation.docx'));
}

//Markdown for exporters
private stripMarkdownForExport(md: string): string {
  let s = (md || '').toString();

  // Remove heading markers at start of lines: "# ", "## ", ... "###### "
  s = s.replace(/^(\s{0,3})#{1,6}\s+/gm, '$1');

  // Remove horizontal rules lines like "---", "***", "___"
  s = s.replace(/^\s{0,3}(-{3,}|\*{3,}|_{3,})\s*$/gm, '');

  // Convert markdown links: [OpenAI](https://openai.com) -> OpenAI
  s = s.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '$1');

  // Remove bold/italic markers (**bold**, *italic*, ***both***)
  s = s.replace(/\*\*\*([^*]+)\*\*\*/g, '$1');
  s = s.replace(/\*\*([^*]+)\*\*/g, '$1');
  s = s.replace(/\*([^*]+)\*/g, '$1');

  // Remove inline code backticks
  s = s.replace(/`([^`]+)`/g, '$1');

  // Collapse extra blank lines a bit
  s = s.replace(/\n{3,}/g, '\n\n');

  return s.trim();
}

/* ========================================================================== */
/*  Exporters (PDF & Doc) (see above)      
/* ========================================================================== */



  showPinStack = false;
  togglePinStack(ev?: MouseEvent) {
    ev?.stopPropagation();
    this.showPinStack = !this.showPinStack;
  }

  jumpToPinned(id?: string) {
    if (!id) return;
    this.showPinStack = false;
    this.scrollToPinnedForId(id);
  }

  onPinsHover(open: boolean) {
    if (this.canHover()) this.showPinStack = open;
  }

  private canHover(): boolean {
    return window.matchMedia?.('(hover: hover) and (pointer: fine)')?.matches ?? false;
  }

  trackMsg = (_: number, m: Message) => m.id ?? (m.timestamp as any);

  private messageKey(msg: Message, index: number): string {
    return msg.id ?? `idx-${index}-${msg.timestamp ?? ''}-${msg.role}`;
  }

  isBlockCollapsed(index: number): boolean {
    const msg = this.currentMessages[index];
    if (!msg) return false;
    return this.collapsedQaBlocks.has(this.messageKey(msg, index));
  }

  shouldHideMessage(msg: Message, index: number): boolean {
    return false;
  }

  toggleQaCollapse(msg: Message, index: number, ev?: MouseEvent): void {
    ev?.stopPropagation();
    const key = this.messageKey(msg, index);
    if (this.collapsedQaBlocks.has(key)) {
      this.collapsedQaBlocks.delete(key);
    } else {
      this.collapsedQaBlocks.add(key);
    }
  }

  private pruneCollapsedQaBlocks(): void {
    const validKeys = new Set<string>();
    this.currentMessages.forEach((message, index) => {
      validKeys.add(this.messageKey(message, index));
    });

    this.collapsedQaBlocks.forEach(key => {
      if (!validKeys.has(key)) {
        this.collapsedQaBlocks.delete(key);
      }
    });
  }

  toggleActionsMenu(msg: Message, ev?: MouseEvent): void {
    ev?.stopPropagation();

    const id = msg.id || null;
    if (!id) return;

    this.openActionsMessageId = this.openActionsMessageId === id ? null : id;
  }

  isActionsMenuOpen(msg: Message): boolean {
    return !!msg.id && this.openActionsMessageId === msg.id;
  }

  onActionPin(msg: Message): void {
    this.togglePinMessage(msg);
    this.openActionsMessageId = null;
  }

  onActionCopy(msg: any, i: number): void {
    this.handleImageAction(msg, i);
    this.showActionToast(msg, 'Copied ✓');
    this.openActionsMessageId = null;
  }

  onActionDelete(msg: Message, index: number): void {
    this.openActionsMessageId = null;

    const nextMessages = [...this.currentMessages];
    const [removed] = nextMessages.splice(index, 1);
    if (!removed) return;

    this.currentMessages = nextMessages;
    this.pinnedMessages = this.currentMessages.filter(m => m.pinned);
    this.pruneCollapsedQaBlocks();
    this.setupMessageObserver();

    if (removed.id && this.actionToastById[removed.id]) {
      delete this.actionToastById[removed.id];
    }

    if (this.currentConversation) {
      const updatedConversation: Conversation = {
        ...this.currentConversation,
        messages: nextMessages,
        updatedAt: new Date() as any
      };
      this.currentConversation = updatedConversation;
      this.chatService.updateConversation(updatedConversation).subscribe();
    }
  }

async onActionCopyToClariChat(msg: Message): Promise<void> {
  try {
    await this.saveMessageToNotes(msg);
    this.showActionToast(msg, 'Added to ClariNotes ✓', 'added');
  } catch (err: any) {
    console.error('Add to ClariNotes failed:', err);

    const msgText = String(err?.message || err || '');

    if (msgText.includes('User not initialized')) {
      this.showActionToast(msg, 'Please log in to save notes', 'warn');
    } else {
      this.showActionToast(msg, 'Failed to save note', 'warn');
    }
  } finally {
    this.openActionsMessageId = null;
  }
}

designMode = false; 

  private injectDesignMessages(): void {
    if (!this.designMode) return;

    const now = new Date().toISOString();

    const demoUser: Message = {
      id: 'demo-user-1',
      role: 'user',
      content: 'Hey Clari, this is a test prompt so I can style the chat bubbles.',
      timestamp: now as any,
      pinned: false,
      modelDisplayName: ''
    } as Message;

    const demoAssistant: Message = {
      id: 'demo-assistant-1',
      role: 'assistant',
      content: `
# H1 — Heading Test

This is a paragraph under H1. It should have clean margin above and below.

---

## H2 — Heading Test

Another paragraph under H2 with **bold text**, *italic text*, and ***bold italic***.

### H3 — Heading Test

Here is a link: [OpenAI](https://openai.com)

#### H4 — Heading Test

##### H5 — Heading Test

###### H6 — Heading Test

---

## Lists

### Unordered List
- Bullet item one
- Bullet item two with *italic*
- Bullet item three with **bold**
  - Nested bullet one
  - Nested bullet two

### Ordered List
1. Numbered item one
2. Numbered item two with \`inline code\`
3. Numbered item three
   1. Nested numbered one
   2. Nested numbered two

---

## Blockquote

> This is a blockquote.  
> It should have padding, a border-left, and look distinct.

---

## Inline Code

Here is inline code: \`npm install clarichat\`

---

## Code Block

\`\`\`ts
function greet(name: string) {
  console.log("Hello " + name);
}
greet("ClariChat");
\`\`\`

---

## Table

| Column A | Column B | Column C |
|---------|----------|----------|
| Row 1   | Data     | Data     |
| Row 2   | More     | Data     |
| Row 3   | Final    | Value    |

---

## Image

![Image Alt Text](https://via.placeholder.com/200)

---

## Text Decorations

This is **bold**, this is *italic*, and this is ~~strikethrough~~.

Here is a <mark>highlighted mark tag</mark> (raw HTML support depends on showdown).
`.trim(),

      timestamp: now as any,
      pinned: false,
      modelDisplayName: 'GPT-5.1 Thinking',
      images: ['https://via.placeholder.com/320x180.png?text=AI+Image']
    } as any;

    this.currentMessages = [demoUser, demoAssistant];

    this.pinnedMessages = this.currentMessages.filter(m => m.pinned);
  }

  @ViewChild('mainSidenav') mainSidenav!: MatSidenav;
  @ViewChild('messagesContainer', { static: false })
  private messagesContainer!: ElementRef<HTMLDivElement>;
  @ViewChild('audioImageInput') audioImageInput!: ElementRef<HTMLInputElement>;
  @ViewChild(MainMenuComponent) mainMenu!: MainMenuComponent;

  // Check if model supports reasoning dropdown in model name (chatgpt, grok, glm, gemini only)
  get showModelNameReasoningDropdown(): boolean {
    const modelId = this.selectedModel;
    const supportedModels = [
      'chatgpt5',
      'chatgpt52',
      'chatgpt523',
      'grok',
      'grok41',
      'gemini',
      'gemini3flash',
      'glm47',
      'zai'
    ];
    return supportedModels.includes(modelId);
  }

  // Toggle model name reasoning dropdown
  toggleModelNameReasoningDropdown(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    if (!this.showModelNameReasoningDropdown) {
      this.modelNameReasoningDropdownOpen = false;
      return;
    }
    this.modelNameReasoningDropdownOpen = !this.modelNameReasoningDropdownOpen;
  }

  // Select reasoning level from model name dropdown
  selectReasoningLevelFromModelName(level: 'auto' | 'instant' | 'thinking' | 'pro', event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    this.reasoningService.setReasoningLevel(level);
    this.modelNameReasoningDropdownOpen = false;
  }

  getReasoningLabel(level: ReasoningLevel): string {
    const labels = {
      auto: 'Auto',
      instant: 'Instant',
      thinking: 'Thinking',
      pro: 'Pro'
    };
    return labels[level] || 'Thinking';
  }

  getReasoningIndicatorArc(): string {
    if (this.selectedReasoningLevel === 'instant' || this.selectedReasoningLevel === 'auto') {
      return '90deg';
    }
    if (this.selectedReasoningLevel === 'thinking') {
      return '180deg';
    }
    return '270deg';
  }

  private ensureChatGpt52ReasoningDefault(modelId: string): void {
    if (modelId !== 'chatgpt52' && modelId !== 'chatgpt523') {
      return;
    }
    this.reasoningService.setReasoningLevel('instant', modelId);
  }

  private ensureGeminiReasoningDefault(modelId: string): void {
    if (modelId !== 'gemini' && modelId !== 'gemini3flash') {
      return;
    }
    const currentLevel = this.reasoningService.getReasoningLevel();
    if (currentLevel === 'auto' || currentLevel === 'instant') {
      this.reasoningService.setReasoningLevel('thinking', modelId);
    }
  }

  // Handle model name button click (only info icon and text, not arrow)
  onModelNameClick(event: Event): void {
    // Only open dialog if clicking on the button itself (not the arrow)
    const target = event.target as HTMLElement;
    if (!target.closest('.model-name-arrow')) {
      this.openAICatalog();
    }
  }

  toggleToolsMenu(): void {
    this.toolsMenuOpen = !this.toolsMenuOpen;
  }

  showScrollToBottom = false;
  loading = false;
  markdownOutput = '';
  error: string | null = null;
  isFading = false;

  isLoggedIn = false;
  private authSub?: Subscription;

  // Model name reasoning dropdown (separate from tools menu)
  modelNameReasoningDropdownOpen = false;
  selectedReasoningLevel: ReasoningLevel = 'thinking';
  

  /* ──────────────────────────────────────────────────────────────
   * Rotating model subtitle (below)
   * ────────────────────────────────────────────────────────────── */

  private openPinForMessageId: string | null = null;

  showMainSubtitle = true;
  selectedModelName: string = 'ChatGPT 5.23';
  isTempChatActive = false;
  audioActive = false;
  micMuted = false;
  private prevModelBeforeAudio: string | null = null;
  private subs: Subscription[] = [];
  messageHistory: string[] = [];
  historyIndex = -1;
  private prismLoaded = false;
  private prism: any = null;

  // Placeholder options with paired text, logo URLs, and names
  placeholderOptions: PlaceholderOption[] = [
    { text: 'Democratizing :The Best AI Access', logoUrl: 'assets/svg/sosexy.png', name: 'ClariChat' },
    { text: 'The OpenAI Flagship', logoUrl: 'assets/logos/openai.svg', name: 'ChatGPT 5.23' },
    { text: 'Industries Top Coding Benchmarks ', logoUrl: 'assets/logos/anthropic.svg', name: 'Opus 4.5' },
    { text: '1 Million Token Context Window', logoUrl: 'assets/logos/x5-baby.png', name: 'Palmyra X5' },
    { text: 'Open Source Muscle - Levels The Playing Field', logoUrl: 'assets/logos/deepseek.svg', name: 'DeepSeek R1' },
    { text: 'Edit Tiny Image Details With Object Masking', logoUrl: 'assets/logos/google.svg', name: 'Gemini Pro 3' },
    { text: 'Build With Live Search', logoUrl: 'assets/logos/xai.svg', name: 'Grok 4' },
    { text: 'Unleash Innovative Enterprise Software ', logoUrl: 'assets/logos/cohere.svg', name: 'Cohere R+' },
    { text: 'Connect The World with Meta AI', logoUrl: 'assets/logos/meta.svg', name: 'Meta AI' }
  ];


  currentLogo: string = this.placeholderOptions[0].logoUrl;
  logoFading = false;
  modelNameFading = false;


  get currentPlaceholderOption(): PlaceholderOption | undefined {
    return this.placeholderOptions[this.placeholderIndex];
  }


  get currentKimName(): string {
    return this.currentPlaceholderOption?.name || 'AI Model';
  }

  // The Original Creator of Best Of Both Worlds (Kim Bielous) The Blue Sister of Pink Brother
  //  - alternate between teal and pink for model name color
  //  - slow letter-by-letter color transition with wave effect
  isShimmering = false;
  private shimmerDirection: 'teal-to-pink' | 'pink-to-teal' = 'teal-to-pink';
  private shimmerLetterIndex = -1; // Track which letter is currently changing
  private shimmerLetters: string[] = [];

  get modelKimColor(): string {
    return this.placeholderIndex % 2 === 0 ? '#008080' : '#800042';
  }

  get modelNameClass(): string {
    if (this.isShimmering) {
      return `model-name shimmer-${this.shimmerDirection}`;
    }
    return 'model-name';
  }
  
  // Split model name into letters for individual animation
  get modelNameLetters(): Array<{ char: string; index: number }> {
    const name = this.currentKimName;
    return Array.from(name).map((char, index) => ({ char, index }));
  }

  // Get color for each letter during shimmer
  getLetterColor(letterIndex: number): string {
    if (!this.isShimmering) return this.modelKimColor;

    const isTealToPink = this.shimmerDirection === 'teal-to-pink';
    const startColor = isTealToPink ? '#008080' : '#800042';
    const endColor = isTealToPink ? '#800042' : '#008080';

    if (letterIndex < this.shimmerLetterIndex) return endColor;
    return startColor;
  }

  // Check if letter should have wave effect (currently transitioning)
  isLetterWaving(letterIndex: number): boolean {
    return this.isShimmering && letterIndex === this.shimmerLetterIndex;
  }

  // ✅ FIX: stable per-letter identity
  trackByLetter = (_: number, item: { char: string; index: number }) => item.index;

  // Get current placeholder text
  get currentPlaceholderText(): string {
    return this.currentPlaceholderOption?.text || '';
  }

  modelCategories = [
    {
      title: '👨‍💻 Elite Code Generation',
      models: [
        { name: 'ChatGPT 5.23', value: 'chatgpt523' },
        { name: 'ChatGPT 5.2', value: 'chatgpt5' },
        { name: 'Sonnet 4.5', value: 'opus45' },
        { name: 'Codestral', value: 'codestralto' },
        { name: 'ChatGPT Mini', value: 'minichatgpt54' },
        { name: 'GLM 4.7', value: 'zai' }
      ]
    },
    {
      title: '🧠 Deep Reasoning Models',
      models: [
        { name: 'Palmrya X5', value: 'palmx' },
        { name: 'Gemini Pro', value: 'gemini' },
        { name: 'Claude Opus 4.5', value: 'opus45' },
        { name: 'Grok', value: 'grok' }
      ]
    },
    {
      title: '🎯 Vision Capable Models',
      models: [
        { name: 'Pixtral', value: 'pixtralimage' }
      ]
    },
    {
      title: '💡 Large Context Windows',
      models: [
        { name: 'DeepSeek V3.2', value: 'deepv3' },
        { name: 'Llama 4', value: 'maverick' },
        { name: 'ChatGPT o3', value: 'o3' }
      ]
    },
     {
      title: '💡 Enterprise Models',
      models: [
        { name: 'Deepseek R1', value: 'deepseek' },
        { name: 'Cohere R+', value: 'cohere' },
        { name: 'Palmyra X5', value: 'palmx' },
        { name: 'Meta Scout', value: 'scout' },
        { name: 'ChatGPT Mini', value: 'minichatgpt54' }
      ]
    },
  ];

  currentPlaceholder = '';
  private placeholderIndex = 0;
  private charIndex = 0;
  private isDeleting = false;
  private typingTimeout?: any;
  private placeholderRotateTimer: any = null;
  private isRotating = false; // Prevent conflicts during rotation

  private pacmanClient = mkPacmanClient({
    rockmanId: '9718dee6f1404543ba0c8ad27c5be405',
    serverUrl: 'https://de-pacman.sam-media.com/api/v2/mstore',
    impressionNumber: 1,
    signalInterval: 1000
  });

  private liveUserIndex: number | null = null;
  private liveAssistantIndex: number | null = null;
  private assistantBuffer = '';
  private aiCatalogRequested = false;

  copiedMessageId: number | null = null;
  pinnedMessages: Message[] = [];
  currentPinnedIndex = -1;
  parallaxLetters = ['C', 'l', 'a', 'r', 'i', 'C', 'h', 'a', 't'];
  curtainFlipActive = false;
  private lastScrollY = 0;
  private lastScrollDirection: 'up' | 'down' | null = null;
  private curtainFlipTimer?: ReturnType<typeof setTimeout>;
  toolsMenuOpen = false;

  constructor(
    private chatService: ChatFireService,
    private sideMenusService: SideMenusService,
    private fb: FormBuilder,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone,
    private hostRef: ElementRef<HTMLElement>,
    private aiFire: AIFireService,
    private tourService: TourService,
    private authService: AuthenticationService,
    private notesService: NotesService,
    private modelSwitchOverlay: ModelSwitchOverlayService,
    private route: ActivatedRoute,
    private reasoningService: ReasoningService,
    private googleDocsService: GoogleDocsService,
    private modelSelectionService: ModelSelectionService
  ) {
    this.form = this.fb.group({
      inputText: [''],
      selectedModel: ['chatgpt523'],
      useWebSearch: [{ value: true, disabled: true }]
    });

    this.o3form = this.fb.group({
      effort: ['low']
    });

    this.converter = new showdown.Converter({
      tables: true,
      simplifiedAutoLink: true,
      strikethrough: true,
      tasklists: true,
      ghCodeBlocks: true,
      smoothLivePreview: true,
      extensions: [
        showdownMarkdownClassesExtension({
          imageClass: 'md-img',
          textClass: 'md-text',
          aiImageClass: 'ai-image',
          aiImageBucketHint: 'media.geniousvision.com'
        })
      ]
    });
  }

      currentOption: PlaceholderOption = this.placeholderOptions[0];

  /* ──────────────────────────────────────────────────────────────
   * Legacy video methods (removed)
   * ────────────────────────────────────────────────────────────── */

  @HostListener('window:scroll')
  onWindowScroll(): void {
    if (this.parallaxTicking) return;
    this.parallaxTicking = true;

    requestAnimationFrame(() => {
      this.parallaxTicking = false;
      this.updateParallaxState();
    });
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.updateParallaxMetrics();
    this.updateParallaxState();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;

    // ─── PIN STACK: close if click is outside ───
    const insidePins = !!target.closest('.pin-stack-anchor');
    if (!insidePins && this.showPinStack) {
      this.ngZone.run(() => (this.showPinStack = false));
    }

    // ─── MODEL NAME REASONING DROPDOWN: close if click is outside ───
    const insideModelNameSelector = !!target.closest('.clarichat-reasoning-fab');
    const insideReasoningMenu = !!target.closest('.model-name-reasoning-menu-wrapper');
    if (!insideModelNameSelector && !insideReasoningMenu && this.modelNameReasoningDropdownOpen) {
      this.ngZone.run(() => (this.modelNameReasoningDropdownOpen = false));
    }

    const insideToolsMenu = !!target.closest('.parallax-tools-menu');
    if (!insideToolsMenu && this.toolsMenuOpen) {
      this.ngZone.run(() => (this.toolsMenuOpen = false));
    }

    // ─── ASSISTANT ACTIONS ───
    const clickedInsideActions = !!target.closest('.assistant-actions');
    if (!clickedInsideActions) {
      this.openActionsMessageId = null;
    }

  }

  openGoogleVideoGenerator() {
    console.log('Google Video Generator');
  }

  openGrokVideoGenerator() {
    console.log('Grok Video Generator');
  }

  private async loadPrismIfNeeded(): Promise<void> {
    if (this.prismLoaded) return;
    if (typeof window === 'undefined' || typeof document === 'undefined') return;

    const core = await import('prismjs');
    await Promise.all([
      import('prismjs/components/prism-markup'),
      import('prismjs/components/prism-markdown'),
      import('prismjs/components/prism-javascript'),
      import('prismjs/components/prism-typescript'),
      import('prismjs/components/prism-json'),
      import('prismjs/components/prism-css'),
      import('prismjs/components/prism-scss'),
      import('prismjs/components/prism-bash'),
      import('prismjs/components/prism-shell-session'),
      import('prismjs/components/prism-python'),
      import('prismjs/components/prism-java'),
      import('prismjs/components/prism-csharp'),
      import('prismjs/components/prism-cpp'),
      import('prismjs/components/prism-yaml'),
      import('prismjs/components/prism-sql')
    ]).catch(() => {});
    this.prism = (window as any).Prism || (core as any);
    this.prismLoaded = !!this.prism;
  }

  async ngAfterViewInit(): Promise<void> {
    if (this.messagesContainer?.nativeElement) {
      this.messagesContainer.nativeElement.addEventListener('scroll', () => {
        const container = this.messagesContainer.nativeElement;
        const threshold = 50;
        const atBottom =
          container.scrollHeight - container.scrollTop - container.clientHeight <= threshold;
        this.userScrolledUp = !atBottom;
        this.showScrollToBottom = !atBottom;
      });
    }
    this.updateParallaxMetrics();
    this.updateParallaxState();
    await this.loadPrismIfNeeded();
    this.highlightAll();
  }

  ngOnInit(): void {
    this.chatService.setChatHistoryNamespace('text').subscribe();

    this.showMainSubtitle = true;
    this.cdr.detectChanges();
    this.selectedModel$ = this.form.get('selectedModel')?.valueChanges;
    this.selectedModel$?.subscribe((modelId: string) => {
      // Store in localStorage for top navbar to detect
      if (modelId) {
        localStorage.setItem('lastSelectedModel', modelId);
        // Also store the model name
        const modelName = this.modelNameFromValue(modelId) || this.selectedModelName || 'Select a Model';
        localStorage.setItem('selectedModelName', modelName);
        // Emit to BehaviorSubject Observable for real-time updates (primary method)
        this.modelSelectionService.setSelectedModelName(modelName);
        // Dispatch custom event for top navbar subscription (legacy support)
        window.dispatchEvent(new CustomEvent('selectedModelChanged', { 
          detail: { modelId, modelName } 
        }));
        this.ensureChatGpt52ReasoningDefault(modelId);
        this.ensureGeminiReasoningDefault(modelId);
      }
    });

    // Listen for openModelSpecDialog event from top navbar
    window.addEventListener('openModelSpecDialog', () => {
      this.openModelSpecDialog();
    });

    window.addEventListener('openAICatalog', () => {
      this.openAICatalog();
    });

    window.addEventListener('clarichatRepeatPrompt', () => {
      this.recallLastMessage();
    });

    window.addEventListener('clarichatExportPdf', () => {
      this.exportConversationToPDF();
    });

    window.addEventListener('clarichatExportDocx', () => {
      this.exportConversationToDOCX();
    });

    window.addEventListener('clarichatStartTour', () => {
      this.startTour();
    });
    
    // Initial check - store current model and emit to service
    const initialModel = this.selectedModel;
    if (initialModel) {
      localStorage.setItem('lastSelectedModel', initialModel);
      const initialModelName = this.selectedModelName || 'Select a Model';
      localStorage.setItem('selectedModelName', initialModelName);
      // Emit initial model name to BehaviorSubject (this will multicast to all subscribers)
      this.modelSelectionService.setSelectedModelName(initialModelName);
      this.ensureChatGpt52ReasoningDefault(initialModel);
      this.ensureGeminiReasoningDefault(initialModel);
    }
    // Subscribe to tour state to show/hide navigation arrows
    this.subs.push(
      this.tourService.currentStep$.subscribe(step => {
        this.isTourActive = step !== null;
        this.tourStepId = step?.elementId ?? null;
        this.cdr.detectChanges();
      })
    );
    this.subs.push(
      this.reasoningService.reasoningLevel$.subscribe((level) => {
        this.selectedReasoningLevel = level;
        this.cdr.detectChanges();
      })
    );

    // Load temporary chat state from localStorage
    this.isTempChatActive = localStorage.getItem('tempChatActive') === 'true';
    
    // Listen for temporary chat toggle events
    window.addEventListener('tempChatToggled', ((event: CustomEvent) => {
      this.isTempChatActive = event.detail.active;
      this.cdr.detectChanges();
    }) as EventListener);

    // Handle conversation ID from route params or query params
    this.route.queryParams.subscribe(params => {
      // If newChat is true, create a fresh conversation
      if (params['newChat'] === 'true') {
        this.chatService.createNewConversation().subscribe(newConv => {
          this.chatService.selectConversation(newConv.id);
        });
        return;
      }
      
      const conversationId = params['conversationId'] || this.id;
      if (conversationId) {
        this.loadConversationById(conversationId);
      }

      if (params['openAICatalog'] && !this.aiCatalogRequested) {
        this.aiCatalogRequested = true;
        setTimeout(() => this.openAICatalog(), 0);
      }
    });

    if (this.id) {
      this.chatService.selectConversation(this.id);
    }

    this.subs.push(
      this.chatService.currentConversation$.subscribe(async conversation => {
        if (this.designMode) {
          this.injectDesignMessages();
          await this.loadPrismIfNeeded();
          setTimeout(() => this.highlightAll(), 0);
          return;
        }

        if (conversation) {
          this.currentConversation = conversation;
          this.currentMessages = conversation.messages;
          this.pruneCollapsedQaBlocks();
          this.scrollToBottomIfNeeded();
          await this.loadPrismIfNeeded();
          setTimeout(() => this.highlightAll(), 0);
          //scroll rail (below)
          setTimeout(() => {
          this.setupMessageObserver();
          //scroll rail (above)
}, 0);
        }
      }),

      this.chatService.isLoading$.subscribe(async loading => {
        this.isLoading = loading;
        if (!loading) {
          await this.loadPrismIfNeeded();
          setTimeout(() => this.highlightAll(), 0);
        }
      })
    );

    this.subs.push(
      this.aiFire.micActive$.subscribe(isActive => {
        this.audioActive = isActive;
        this.cdr.markForCheck();
      })
    );

    // Subscribe to user transcription (real-time)
    this.subs.push(
      this.aiFire.userTranscriptDelta$.subscribe(transcript => {
        if (transcript && transcript.trim().length) {
          this.upsertLiveUserMessage(transcript);
        }
      })
    );

    this.subs.push(
      this.aiFire.userTranscriptDone$.subscribe(transcript => {
        if (transcript && transcript.trim().length) {
          this.finalizeLiveUserMessage();
        }
      })
    );

    this.subs.push(
      this.aiFire.textDelta$.subscribe(delta => {
        if (typeof delta === 'string' && delta.length) {
          this.assistantBuffer += delta;
          this.upsertLiveAssistantMessage(this.assistantBuffer);
        }
      }),
      this.aiFire.textDone$.subscribe(() => {
        this.finalizeLiveAssistantMessage();
      })
    );

    this.form.get('inputText')?.valueChanges.subscribe(value => {
      this.convertMarkdown(value);
    });

    const currentModel = this.modelCategories
      .flatMap(cat => cat.models)
      .find(m => m.value === this.selectedModel);
    this.selectedModelName = currentModel?.name || 'Select a Model';
    // Store in localStorage for top navbar
    localStorage.setItem('selectedModelName', this.selectedModelName);
    // Emit to BehaviorSubject Observable for real-time updates
    this.modelSelectionService.setSelectedModelName(this.selectedModelName);


    this.authSub = this.authService.currentUser$.subscribe(user => {
      const loggedIn = !!user;
      this.isLoggedIn = loggedIn;

      const ctl = this.form.get('useWebSearch');
      if (!ctl) return;

      if (loggedIn) {
        ctl.enable({ emitEvent: false });
      } else {
        ctl.setValue(false, { emitEvent: false });
        ctl.disable({ emitEvent: false });
      }

      try {
        this.cdr.markForCheck();
      } catch {}
    });

    this.showMainSubtitle = true;
  this.cdr.detectChanges();

  
  // ──────────────────────────────────────────────
  // Placeholder + rotation (below)
  // ──────────────────────────────────────────────
  setTimeout(() => {
    this.showMainSubtitle = false;
    this.cdr.detectChanges();
    //(no fixed interval)
    this.startPerfectPlaceholderCycle();
  });
  }

  onPinClick(msg: Message, event: MouseEvent): void {
    event.stopPropagation();
    this.togglePinMessage(msg);
    if (msg.pinned) {
      const idx = this.pinnedMessages.findIndex(m => m.id === msg.id);
      this.currentPinnedIndex = idx >= 0 ? idx : this.currentPinnedIndex;
    } else {
      if (this.pinnedMessages.length === 0) this.currentPinnedIndex = -1;
      else this.currentPinnedIndex = Math.min(this.currentPinnedIndex, this.pinnedMessages.length - 1);
    }
  }

  ngOnDestroy(): void {
    if (this.typingTimeout) clearTimeout(this.typingTimeout);
    this.subs.forEach(s => s.unsubscribe());

    if (this.curtainFlipTimer) {
      clearTimeout(this.curtainFlipTimer);
    }


    if (this.placeholderRotateTimer) {
      clearInterval(this.placeholderRotateTimer);
      this.placeholderRotateTimer = null;
    }

    if (this.placeholderRotateTimer) {
      clearInterval(this.placeholderRotateTimer);
      this.placeholderRotateTimer = null;
    }

    // ──────────────────────────────────────────────
    // Placeholder + rotation (below)
    // ──────────────────────────────────────────────
    this.authSub?.unsubscribe();
    this.stopPerfectPlaceholderCycle();
    this.subs.forEach(s => s.unsubscribe());
    this.authSub?.unsubscribe();

    this.msgObserver?.disconnect(); //scroll rail
  }

  // ===================== TOUR HANDLER =====================
  startTour() {
    const steps: TourStep[] = [
      { elementId: 'brandingClariChat', title: '<span class="first-line">The Future Awaits</span>', description: '<span class="line-beige">One Prompt</span><br><br><span class="line-beige">Competitive Accuracy</span><br><br><span class="line-beige">1-6 Scored Answers</span><br><br><span class="line-beige">Always Context</span><br><br><span class="line-beige">Top Selection of AI Models</span><br><br><span class="line-beige last-line">The AI Democracy</span>' },
      { elementId: 'previousPromptsBtn', title: 'My Prompts', description: 'Your best prompts live here. Reuse them instantly.' },
      { elementId: 'modelSpecsSection', title: 'Bio', description: 'Tap this — it reveals what your AI is REALLY capable of.' },
      { elementId: 'aiCatalogBtn', title: 'AI Models', description: 'This is where the real advantage lives — the right model can deliver 10× better results.' },
      { elementId: 'modelSpecsSection', title: 'Bio', description: 'Tap this — it reveals what your AI is REALLY capable of.' },
      { elementId: 'moreSection', title: 'More Section', description: 'Hidden power tools inside. You’re gonna want this.' },
      { elementId: 'effort', title: 'Reasoning Depth', description: 'Dial up the brainpower. Go from quick to genius-level.' },
      { elementId: 'chatInputTextarea', title: 'Your Text Area', description: 'Drop your idea here — we’ll turn it into something unreal.' },
      { elementId: 'sendMessageButton', title: 'Send Button', description: 'Hit send and watch the AI come alive.' },
      { elementId: 'attachImageButton', title: 'Attach Image', description: 'Upload a photo — the AI will dissect it like a pro.' },
      { elementId: 'micButton', title: 'Voice Input', description: 'Talk instead of type. It feels futuristic.' },
      { elementId: 'advisorFab', title: 'Advisor Button', description: 'This floating Advisor button opens your Coach menu. You can drag it anywhere on the screen, and it stays accessible while you work.' },
      { elementId: 'placeholderNavArrows', title: 'Choose a Model', description: 'Preview a few featured models here. Open the AI Selector to access many more.' },
      { elementId: 'profileMenuSection', title: 'Your Profile', description: 'Your command center — settings, upgrades, everything.' },
      { elementId: 'loginLink', title: 'Log In', description: 'Log in to unlock the full ClariChat experience.' },
      { elementId: 'aiExecsLink', title: 'GroupChat', description: 'Ask once. Get answers from multiple AIs. Instant advantage.' },
      { elementId: 'menuToggleBtn', title: 'Side Menu', description: 'Open this — the real power features are in here.', openMenu: true },
      { elementId: 'claritools', title: 'Clarity ToolKit', description: 'This is your cheat code. Don’t skip it.' },
    ];
    this.tourService.startTour(steps);
  }

  // ===================== Live transcript helpers =====================
  private upsertLiveUserMessage(text: string): void {
    if (this.liveUserIndex == null) {
      this.currentMessages = [
        ...this.currentMessages,
        {
          role: 'user',
          content: text,
          timestamp: new Date().toISOString() as any
        } as Message
      ];
      this.liveUserIndex = this.currentMessages.length - 1;
    } else {
      this.currentMessages[this.liveUserIndex] = {
        ...this.currentMessages[this.liveUserIndex],
        content: text
      } as Message;
    }
    this.cdr.detectChanges();
    this.scrollToBottomIfNeeded();
  }

  private finalizeLiveUserMessage(): void {
    if (this.liveUserIndex != null) {
      this.liveUserIndex = null;
      this.cdr.detectChanges();
      this.scrollToBottomIfNeeded();
    }
  }

  private upsertLiveAssistantMessage(text: string): void {
    if (this.liveAssistantIndex == null) {
      this.currentMessages = [
        ...this.currentMessages,
        {
          role: 'assistant',
          content: text,
          timestamp: new Date().toISOString() as any,
          modelDisplayName: 'GPT Realtime (Audio)'
        } as Message
      ];
      this.liveAssistantIndex = this.currentMessages.length - 1;
    } else {
      this.currentMessages[this.liveAssistantIndex] = {
        ...this.currentMessages[this.liveAssistantIndex],
        content: text
      } as Message;
    }
    this.cdr.detectChanges();
    this.scrollToBottomIfNeeded();
  }

  private finalizeLiveAssistantMessage(): void {
    if (this.liveAssistantIndex != null) {
      this.assistantBuffer = '';
      this.liveAssistantIndex = null;
      this.cdr.detectChanges();
      this.scrollToBottomIfNeeded();
    }
  }



  private modelNameFromValue(val: string): string | null {
    if (val === 'gpt_realtime') return 'GPT Realtime (Audio)';
    if (val === 'gpt_realtime_text') return 'GPT Realtime (Text)';
    if (val === 'gpt4o_audio_realtime') return 'GPT Realtime (Audio)';
    if (val === 'gpt4o_audio_text') return 'GPT Realtime (Text)';
    const found = this.modelCategories.flatMap(cat => cat.models).find(m => m.value === val);
    return found?.name || null;
  }

  openAICatalog(): void {
    const ref = this.dialog.open(AiCatalogComponent, {
      panelClass: 'ai-catalog-dialog-panel',
      width: '900px',
      maxWidth: '98vw',
      height: 'auto',
      maxHeight: '95vh',
      data: {
        categories: this.modelCategories,
        selectedModel: this.selectedModel,
        selectedModelName: this.selectedModelName
      }
    });

ref.afterClosed().subscribe((result?: { name: string; value: string }) => {
  if (!result) return;

  // 🔹 1) Switch the model (you already do this)
  this.form.get('selectedModel')?.setValue(result.value);
  this.selectedModelName =
    result.name || this.modelNameFromValue(result.value) || 'Select a Model';

  // Store in localStorage for top navbar
  localStorage.setItem('lastSelectedModel', result.value);
  localStorage.setItem('selectedModelName', this.selectedModelName);
  // Emit to BehaviorSubject Observable for real-time updates
  this.modelSelectionService.setSelectedModelName(this.selectedModelName);
  // Dispatch custom event for top navbar subscription (legacy support)
  window.dispatchEvent(new CustomEvent('selectedModelChanged', { 
    detail: { modelId: result.value, modelName: this.selectedModelName } 
  }));

  // 🔹 2) SHOW THE "YOU ARE NOW USING XYZ" MOMENT
  this.modelSwitchOverlay.show({
    name: this.selectedModelName,
    value: result.value,
  });

  this.cdr.markForCheck();
});
  }

  // startTypingEffect() {
  //   // Don't continue typing if we're in the middle of a rotation
  //   if (this.isRotating) return;

  //   const typingSpeed = 120;
  //   const deletingSpeed = 100;
  //   const pauseAfterTyping = 1500;
  //   const pauseAfterDeleting = 500;

  //   const currentOption = this.placeholderOptions[this.placeholderIndex];
  //   if (!currentOption) return;
  //   const currentString = currentOption.text || '';

  //   if (!this.isDeleting) {
  //     this.charIndex++;
  //     this.currentPlaceholder = currentString.substring(0, this.charIndex);
  //     this.cdr.detectChanges();

  //     if (this.charIndex === currentString.length) {
  //       this.isDeleting = true;
  //       this.typingTimeout = setTimeout(() => this.startTypingEffect(), pauseAfterTyping);
  //     } else {
  //       this.typingTimeout = setTimeout(() => this.startTypingEffect(), typingSpeed);
  //     }
  //   } else {
  //     this.charIndex--;
  //     this.currentPlaceholder = currentString.substring(0, this.charIndex);
  //     this.cdr.detectChanges();

  //     if (this.charIndex === 0) {
  //       this.isDeleting = false;
  //       // Don't auto-rotate here - let the timer handle it
  //       this.typingTimeout = setTimeout(() => this.startTypingEffect(), pauseAfterDeleting);
  //     } else {
  //       this.typingTimeout = setTimeout(() => this.startTypingEffect(), deletingSpeed);
  //     }
  //   }
  // }


// ──────────────────────────────────────────────
// Placeholder + rotation (smooth 100 WPM)
// ──────────────────────────────────────────────

private readonly placeholderHoldMs = 2200;
private readonly modelPlaceholderHoldMs = 2200;
private readonly fadeDurationMs = 250;
private readonly shimmerStepMs = 70;
private readonly typingSpeedMs = 120;
private readonly deletingSpeedMs = 100;
private readonly pauseAfterTypingMs = 1500;
private readonly pauseAfterDeletingMs = 500;

private placeholderTimer: any = null;
private shimmerTimer: any = null;

private destroyed = false;
private cycleToken = 0;

private clearPlaceholderTimer(): void {
  if (this.placeholderTimer) {
    clearTimeout(this.placeholderTimer);
    this.placeholderTimer = null;
  }
}

private clearShimmerTimer(): void {
  if (this.shimmerTimer) {
    clearTimeout(this.shimmerTimer);
    this.shimmerTimer = null;
  }
}

private schedulePlaceholder(token: number, delayMs: number, fn: () => void): void {
  this.clearPlaceholderTimer();

  // Run outside Angular so we don't trigger app-wide CD on every tick
  this.ngZone.runOutsideAngular(() => {
    this.placeholderTimer = setTimeout(() => {
      if (this.destroyed || token !== this.cycleToken) return;
      this.ngZone.run(() => fn());
    }, delayMs);
  });
}

private scheduleShimmer(token: number, delayMs: number, fn: () => void): void {
  this.clearShimmerTimer();

  this.ngZone.runOutsideAngular(() => {
    this.shimmerTimer = setTimeout(() => {
      if (this.destroyed || token !== this.cycleToken) return;
      this.ngZone.run(() => fn());
    }, delayMs);
  });
}

private startPerfectPlaceholderCycle(): void {
  this.destroyed = false;
  this.cycleToken++;

  this.placeholderIndex = 0;
  this.applyPlaceholderOption(this.placeholderIndex);
  this.currentPlaceholder = '';

  this.logoFading = false;
  this.modelNameFading = false;

  this.stopShimmer();
  this.startShimmer(this.cycleToken);

  this.cdr.detectChanges();

  this.runCycle(this.cycleToken);
}

private stopPerfectPlaceholderCycle(): void {
  this.destroyed = true;
  this.cycleToken++;
  this.clearPlaceholderTimer();
  this.clearShimmerTimer();
  this.stopShimmer();
}

private transitionToNextOption(token: number, immediate = false): void {
  if (this.destroyed || token !== this.cycleToken) return;

  const prevIndex = this.placeholderIndex;
  const nextIndex = (prevIndex + 1) % this.placeholderOptions.length;

  this.logoFading = true;
  this.modelNameFading = true;
  this.stopShimmer();
  this.cdr.detectChanges();

  const fadeMs = immediate ? 60 : this.fadeDurationMs;

  this.schedulePlaceholder(token, fadeMs, () => {
    this.placeholderIndex = nextIndex;
    this.applyPlaceholderOption(this.placeholderIndex);
    this.currentPlaceholder = '';

    this.logoFading = false;
    this.modelNameFading = false;

    this.shimmerDirection = prevIndex % 2 === 0 ? 'teal-to-pink' : 'pink-to-teal';
    this.startShimmer(token);

    this.cdr.detectChanges();

    this.runCycle(token);
  });
}

fastForwardTransition(): void {
  if (this.destroyed) return;

  const token = ++this.cycleToken;
  this.clearPlaceholderTimer();
  this.clearShimmerTimer();
  this.stopShimmer();

  this.currentPlaceholder = '';
  this.cdr.detectChanges();

  this.transitionToNextOption(token, true);
}

private startShimmer(token: number): void {
  this.stopShimmer();

  this.isShimmering = true;
  this.shimmerLetters = this.currentKimName.split('');
  this.shimmerLetterIndex = -1;

  this.stepShimmer(token);
}

private stepShimmer(token: number): void {
  if (this.destroyed || token !== this.cycleToken || !this.isShimmering) return;

  this.shimmerLetterIndex++;

  // Let index reach length so the last letter completes its transition
  if (this.shimmerLetterIndex >= this.shimmerLetters.length) {
    this.stopShimmer();
    this.cdr.detectChanges();
    return;
  }

  this.cdr.detectChanges();

  this.scheduleShimmer(token, this.shimmerStepMs, () => this.stepShimmer(token));
}

private stopShimmer(): void {
  this.clearShimmerTimer();
  this.isShimmering = false;
  this.shimmerLetterIndex = -1;
}

private runCycle(token: number): void {
  if (this.destroyed || token !== this.cycleToken) return;

  const option = this.placeholderOptions[this.placeholderIndex];
  if (!option) return;

  const fullText = option.text || '';

  this.typeForward(fullText, 0, token, () => {
    this.schedulePlaceholder(token, this.pauseAfterTypingMs, () => {
      this.deleteBackward(fullText, fullText.length, token, () => {
        this.schedulePlaceholder(token, this.pauseAfterDeletingMs, () => {
          this.transitionToNextOption(token);
        });
      });
    });
  });
}

private typeForward(fullText: string, idx: number, token: number, done: () => void): void {
  if (this.destroyed || token !== this.cycleToken) return;

  this.currentPlaceholder = fullText.slice(0, idx);
  this.cdr.detectChanges();

  if (idx >= fullText.length) {
    done();
    return;
  }

  this.schedulePlaceholder(token, this.typingSpeedMs, () => {
    this.typeForward(fullText, idx + 1, token, done);
  });
}

private deleteBackward(fullText: string, idx: number, token: number, done: () => void): void {
  if (this.destroyed || token !== this.cycleToken) return;

  this.currentPlaceholder = fullText.slice(0, idx);
  this.cdr.detectChanges();

  if (idx <= 0) {
    done();
    return;
  }

  this.schedulePlaceholder(token, this.deletingSpeedMs, () => {
    this.deleteBackward(fullText, idx - 1, token, done);
  });
}

// Keep this small + safe: only updates the active option + logo.
// Typing loop owns currentPlaceholder.
private applyPlaceholderOption(index: number): void {
  const option = this.placeholderOptions[index];
  if (!option) return;

  this.currentOption = option;
  this.currentLogo = option.logoUrl;
  const shadeBase = 232;
  const shadeStep = 12;
  const gray = Math.max(120, shadeBase - index * shadeStep);
  const grayDark = Math.max(95, gray - 20);
  const greenHue = 140 + (index * 7) % 30;
  const greenStart = `hsl(${greenHue}, 45%, 38%)`;
  const greenEnd = `hsl(${greenHue + 10}, 55%, 28%)`;
  this.hostRef.nativeElement.style.setProperty('--placeholder-gray', `rgb(${gray}, ${gray}, ${gray})`);
  this.hostRef.nativeElement.style.setProperty('--placeholder-gray-dark', `rgb(${grayDark}, ${grayDark}, ${grayDark})`);
  this.hostRef.nativeElement.style.setProperty('--placeholder-green-start', greenStart);
  this.hostRef.nativeElement.style.setProperty('--placeholder-green-end', greenEnd);
  this.cdr.detectChanges();
}

  get selectedModel(): string {
    return this.form.get('selectedModel')?.value || 'chatgpt523';
  }

  onKeyDown(event: KeyboardEvent): void {
  const el = event.target as HTMLTextAreaElement | null;
  if (!el) return;

  // ArrowUp
  if (event.key === 'ArrowUp' && !event.shiftKey) {
    const start = el.selectionStart ?? 0;
    const end = el.selectionEnd ?? 0;

    if (start === 0 && end === 0) {
      event.preventDefault();
      this.recallPreviousMessage();
    }
    return;
  }

  // ArrowDown 
  if (event.key === 'ArrowDown' && !event.shiftKey) {
    const len = el.value?.length ?? 0;
    const start = el.selectionStart ?? 0;
    const end = el.selectionEnd ?? 0;

    if (start === len && end === len) {
      event.preventDefault();
      this.recallNextMessage();
    }
    return;
  }
}

  private composingAtTop = false;
  hasSentMessage = false;

composerExpanded = false;
onEnter(event: KeyboardEvent): void {
  event.preventDefault();
  event.stopPropagation();
  this.sendMessage();
  this.composerExpanded = true;
}

onShiftEnter(event: KeyboardEvent, el: HTMLTextAreaElement): void {
  event.preventDefault();
  event.stopPropagation();
  (event as any).stopImmediatePropagation?.();

  // Tell autoresize: do NOT jump to bottom
  this.composingAtTop = true;

  // Preserve current visual scroll position (so it doesn't jump)
  const prevScrollTop = el.scrollTop;

  // Insert newline at caret
  const start = el.selectionStart ?? this.userInput.length;
  const end = el.selectionEnd ?? this.userInput.length;

  this.userInput =
    this.userInput.slice(0, start) + '\n' + this.userInput.slice(end);

  const nextPos = start + 1;

  // Wait for ngModel to update textarea value, then restore caret/scroll
  setTimeout(() => {
    el.focus();
    el.setSelectionRange(nextPos, nextPos);

    // Resize, but keep our scroll pinned
    this.autoResize(el, prevScrollTop);

    // keep "top composing" mode on so repeated Shift+Enter keeps pushing down
    // (we'll turn it off when the user scrolls manually)
  });
}


  // ---------- Copy / images ----------
  async copyMessageContent(message: any, index: number) {
    try {
      let imageSource: string | null = null;

      if (message.image && message.image.length > 100) {
        imageSource = message.image;
      } else if (
        message.images &&
        message.images.length > 0 &&
        message.images[0].length > 100
      ) {
        imageSource = message.images[0];
      }

      if (imageSource) {
        if (
          imageSource.startsWith('data:') ||
          (imageSource.length > 200 && !imageSource.startsWith('http'))
        ) {
          const dataUrl = imageSource.startsWith('data:')
            ? imageSource
            : 'data:image/png;base64,' + imageSource;

          const blob = this.dataURLtoBlob(dataUrl, 'image/png');

          try {
            await navigator.clipboard.write([new ClipboardItem({ [blob.type]: blob })]);
          } catch (err) {
            console.warn('Base64 image copy failed, fallback to base64 text', err);
            await navigator.clipboard.writeText(dataUrl);
          }
        } else if (imageSource.startsWith('http')) {
          try {
            const res = await fetch(imageSource, { mode: 'cors' });
            const blob = await res.blob();
            await navigator.clipboard.write([new ClipboardItem({ [blob.type]: blob })]);
          } catch (err) {
            console.warn('HTTPS image copy failed, fallback to URL text', err);
            await navigator.clipboard.writeText(imageSource);
          }
        } else {
          await navigator.clipboard.writeText(imageSource);
        }
      } else {
        await navigator.clipboard.writeText(this.normalizeMessageContent(message?.content));
      }

      this.copiedMessageId = index;
      setTimeout(() => (this.copiedMessageId = null), 2000);
    } catch (err) {
      console.error('Failed to copy message content:', err);
    }
  }

  private dataURLtoBlob(dataUrl: string, mimeOverride?: string): Blob {
    const [header, base64Data] = dataUrl.split(',');
    if (!base64Data) throw new Error('Invalid data URL');

    const mimeMatch = header.match(/:(.*?);/);
    const mime = mimeOverride || (mimeMatch ? mimeMatch[1] : 'image/png');

    const byteString = atob(base64Data);
    const u8arr = new Uint8Array(byteString.length);
    for (let i = 0; i < byteString.length; i++) {
      u8arr[i] = byteString.charCodeAt(i);
    }

    return new Blob([u8arr], { type: mime });
  }

  handleImageAction(msg: any, i: number) {
    if (msg.modelId === 'grokimage') {
      const imageUrl = msg.image || msg.images?.[0];
      if (imageUrl) {
        const newTab = window.open(imageUrl, '_blank');
        if (!newTab) {
          const link = document.createElement('a');
          link.href = imageUrl;
          link.target = '_blank';
          link.download = 'grok-image.png';
          link.click();
        }
      }
    } else {
      this.copyMessageContent(msg, i);
    }
  }

  downloadGrokImage(url: string, filename: string = 'grok-image.png') {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  // ─────────── Dialog openers ───────────
  openDeepseekDialog() {
    this.dialog.open(DeepseekComponent, {
      width: '900px',
      maxWidth: '99vw',
      height: 'auto',
      maxHeight: '98vh',
      panelClass: 'deepseek-dialog-panel'
    });
  }

       openDeepv3Dialog() {
    this.dialog.open(Deepseekv3Component, {
      width: '900px',
      maxWidth: '99vw',
      height: 'auto',
      maxHeight: '98vh',
      panelClass: 'Sonnet4-dialog-panel'
    });
  }


  openChatGpt5Dialog() {
    this.dialog.open(GptFiveComponent, {
      width: '900px',
      maxWidth: '99vw',
      height: 'auto',
      maxHeight: '98vh',
      panelClass: 'deepseek-dialog-panel'
    });
  }


  openGeminiDialog() {
    this.dialog.open(GeminiProComponent, {
      width: '900px',
      maxWidth: '99vw',
      height: 'auto',
      maxHeight: '98vh',
      panelClass: 'deepseek-dialog-panel'
    });
  }

  openMaverickDialog() {
    this.dialog.open(MaverickComponent, {
      width: '900px',
      maxWidth: '99vw',
      height: 'auto',
      maxHeight: '98vh',
      panelClass: 'deepseek-dialog-panel'
    });
  }

  openScoutDialog() {
    this.dialog.open(ScoutComponent, {
      width: '900px',
      maxWidth: '99vw',
      height: 'auto',
      maxHeight: '98vh',
      panelClass: 'deepseek-dialog-panel'
    });
  }

  openJambaDialog() {
    this.dialog.open(JambaComponent, {
      width: '900px',
      maxWidth: '99vw',
      height: 'auto',
      maxHeight: '98vh',
      panelClass: 'deepseek-dialog-panel'
    });
  }

  openSonnet4Dialog() {
    this.dialog.open(SonnetFourComponent, {
      width: '900px',
      maxWidth: '99vw',
      height: 'auto',
      maxHeight: '98vh',
      panelClass: 'Sonnet4-dialog-panel'
    });
  }

  openChatGpt41Dialog() {
    this.dialog.open(GptFourOneComponent, {
      width: '900px',
      maxWidth: '99vw',
      height: 'auto',
      maxHeight: '98vh',
      panelClass: 'Sonnet4-dialog-panel'
    });
  }

  openChatGptMiniDialog() {
    this.dialog.open(FourOMiniComponent, {
      width: '900px',
      maxWidth: '99vw',
      height: 'auto',
      maxHeight: '98vh',
      panelClass: 'Sonnet4-dialog-panel'
    });
  }

  openCohereRPlusiDialog() {
    this.dialog.open(CohereRPlusComponent, {
      width: '900px',
      maxWidth: '99vw',
      height: 'auto',
      maxHeight: '98vh',
      panelClass: 'Sonnet4-dialog-panel'
    });
  }

  openLlamaDialog() {
    this.dialog.open(LlamaThreePointThreeComponent, {
      width: '900px',
      maxWidth: '99vw',
      height: 'auto',
      maxHeight: '98vh',
      panelClass: 'Sonnet4-dialog-panel'
    });
  }

  openMistralLargeDialog() {
    this.dialog.open(MistralLargeComponent, {
      width: '900px',
      maxWidth: '99vw',
      height: 'auto',
      maxHeight: '98vh',
      panelClass: 'Sonnet4-dialog-panel'
    });
  }

  openPixtralDialog() {
    this.dialog.open(PixtralImageComponent, {
      width: '900px',
      maxWidth: '99vw',
      height: 'auto',
      maxHeight: '98vh',
      panelClass: 'Sonnet4-dialog-panel'
    });
  }

  openO3Dialog() {
    this.dialog.open(OThreeComponent, {
      width: '900px',
      maxWidth: '99vw',
      height: 'auto',
      maxHeight: '98vh',
      panelClass: 'Sonnet4-dialog-panel'
    });
  }

  openOpusDialog() {
    this.dialog.open(OpusFourComponent, {
      width: '900px',
      maxWidth: '99vw',
      height: 'auto',
      maxHeight: '98vh',
      panelClass: 'opus-dialog-panel'
    });
  }

  openDalEDialog() {
    this.dialog.open(DalEComponent, {
      width: '900px',
      maxWidth: '99vw',
      height: 'auto',
      maxHeight: '98vh',
      panelClass: 'deepseek-dialog-panel'
    });
  }

  openGrokImageDialog() {
    this.dialog.open(GrokImageComponent, {
      width: '900px',
      maxWidth: '99vw',
      height: 'auto',
      maxHeight: '98vh',
      panelClass: 'Sonnet4-dialog-panel'
    });
  }

  openStabilityDialog() {
    this.dialog.open(StabilityComponent, {
      width: '900px',
      maxWidth: '99vw',
      height: 'auto',
      maxHeight: '98vh',
      panelClass: 'Sonnet4-dialog-panel'
    });
  }

    openHaikuDialog() {
    this.dialog.open(HaikuComponent, {
      width: '900px',
      maxWidth: '99vw',
      height: 'auto',
      maxHeight: '98vh',
      panelClass: 'Sonnet4-dialog-panel'
    });
  }

     openPalmryaDialog() {
    this.dialog.open(PalmyraComponent, {
      width: '900px',
      maxWidth: '99vw',
      height: 'auto',
      maxHeight: '98vh',
      panelClass: 'Sonnet4-dialog-panel'
    });
  }

       openNovaLiteDialog() {
    this.dialog.open(NovaLiteComponent, {
      width: '900px',
      maxWidth: '99vw',
      height: 'auto',
      maxHeight: '98vh',
      panelClass: 'Sonnet4-dialog-panel'
    });
  }

    openNovaProDialog() {
    this.dialog.open(NovaProComponent, {
      width: '900px',
      maxWidth: '99vw',
      height: 'auto',
      maxHeight: '98vh',
      panelClass: 'Sonnet4-dialog-panel'
    });
  }

      openGLMDialog() {
    this.dialog.open(GlmComponent, {
      width: '900px',
      maxWidth: '99vw',
      height: 'auto',
      maxHeight: '98vh',
      panelClass: 'Sonnet4-dialog-panel'
    });
  }


  openModelSpecDialog() {
    if (!this.selectedModel) return;
    
    const dialogMap: { [key: string]: () => void } = {
      'opus45': () => this.openOpusDialog(),
      'opus': () => this.openOpusDialog(),
      'sonnet45': () => this.openSonnet4Dialog(),
      'chatgpt5': () => this.openChatGpt5Dialog(),
      'chatgpt523': () => this.openChatGpt5Dialog(),
      'chatgpt41': () => this.openChatGpt41Dialog(),
      'gpt41': () => this.openChatGpt41Dialog(),
      'chatgptmini': () => this.openChatGptMiniDialog(),
      'gpt4omini': () => this.openChatGptMiniDialog(),
      'grok41': () => this.openGrokImageDialog(),
      'grokimage': () => this.openGrokImageDialog(),
      'gemini': () => this.openGeminiDialog(),
      'maverick': () => this.openMaverickDialog(),
      'scout': () => this.openScoutDialog(),
      'jamba': () => this.openJambaDialog(),
      'llama': () => this.openLlamaDialog(),
      'llama3': () => this.openLlamaDialog(),
      'cohere': () => this.openCohereRPlusiDialog(),
      'mistrallarge': () => this.openMistralLargeDialog(),
      'mistral': () => this.openMistralLargeDialog(),
      'pixtralimage': () => this.openPixtralDialog(),
      'pixtral': () => this.openPixtralDialog(),
      'o3': () => this.openO3Dialog(),
      'dale': () => this.openDalEDialog(),
      'stability': () => this.openStabilityDialog(),
      'haiku45': () => this.openHaikuDialog(),
      'haiku': () => this.openHaikuDialog(),
      'palmx': () => this.openPalmryaDialog(),
      'palmyra': () => this.openPalmryaDialog(),
      'nova2': () => this.openNovaLiteDialog(),
      'novalite': () => this.openNovaLiteDialog(),
      'novapro': () => this.openNovaProDialog(),
      'zai': () => this.openGLMDialog(),
      'deepseek': () => this.openDeepseekDialog(),
      'deepv3': () => this.openDeepv3Dialog()
    };

    const openDialog = dialogMap[this.selectedModel];
    if (openDialog) {
      openDialog();
    }
  }

  // ---------- File/image handling ----------
  onFileSelected(event: Event): void {
    const fileInput = event.target as HTMLInputElement;
    const file = fileInput.files?.[0];
    if (!file) return;
    this.selectedFile = file;
    const reader = new FileReader();
    reader.onload = e => {
      this.selectedImagePreview = e.target?.result as string;
    };
    reader.readAsDataURL(file);
  }

  async onAudioImageSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    const previewUrl = await this.readAsDataUrl(file);
    this.pushUserImageMessage(previewUrl);

    try {
      const instruction =
        'Please analyse this image and explain the key details relevant to our conversation.';
      await firstValueFrom(this.aiFire.sendRealtimeImage(file, instruction));
    } catch (err) {
      console.error('Failed to send image into realtime session:', err);
    } finally {
      if (this.audioImageInput?.nativeElement) {
        this.audioImageInput.nativeElement.value = '';
      }
      setTimeout(() => this.scrollToBottom(), 0);
    }
  }

  private readAsDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(new Error('Failed to read image.'));
      reader.onload = () => resolve(String(reader.result || ''));
      reader.readAsDataURL(file);
    });
  }

  private pushUserImageMessage(dataUrl: string) {
    const msg: Partial<Message> = {
      role: 'user',
      content: '',
      image: dataUrl,
      timestamp: new Date().toISOString() as any
    };
    this.currentMessages = [...this.currentMessages, msg as Message];
    this.cdr.detectChanges();
  }

  // ===================== Voice entry point (REAL-TIME TRANSCRIPTION) =====================
  async onAudioIconClick(): Promise<void> {
    // Check if already active - toggle off if so
    if (this.audioActive) {
      await firstValueFrom(this.chatService.endRealtimeAudioTurn());
      return;
    }

    // Start real-time audio session with transcription in message container
    try {
      await firstValueFrom(this.chatService.beginRealtimeAudioTurn());
      // User transcription will appear in real-time via userTranscriptDelta$ subscription
      // Assistant responses will appear via textDelta$ subscription
    } catch (error) {
      console.error('Failed to start real-time audio:', error);
    }
  }

  toggleMic(): void {
    this.micMuted = !this.micMuted;
    try {
      this.chatService.muteRealtimeMic(this.micMuted);
    } catch {}
  }

  async stopVoice(): Promise<void> {
    try {
      await firstValueFrom(this.chatService.stopRealtimeAudio());
    } catch {}
  }

  // ===================== Core send =====================
  async sendMessage(): Promise<void> {
    const model = this.selectedModel;

    this.resetTextarea(); 

    if (this.isLoading) return;
    if (!this.userInput.trim()) return;

    const prompt = (this.userInput || '').trim();

    if (prompt) {
      this.messageHistory.push(prompt);
      this.historyIndex = -1;
    }

    this.userInput = '';

    const useWeb = this.form.get('useWebSearch')?.value === true;

    if (model === 'pixtralimage') {
      this.chatService.sendPixtralMessage(prompt, this.selectedFile);
      this.resetUploadState();
      return;
    }

    // Pass image for models that support it (gemini, sonnet45, etc.)
    const imageFile = (model === 'gemini' || model === 'sonnet45' || model === 'opus45' || model === 'haiku45') ? this.selectedFile : undefined;
    this.chatService.sendMessage(prompt, model, useWeb, imageFile).subscribe();
    this.resetUploadState();

    setTimeout(() => this.scrollToBottom(), 50);

    await this.loadPrismIfNeeded();
    setTimeout(() => this.highlightAll(), 0);
  
    this.forceHighlightLastTick(); //scroll tick


  if (!this.userInput.trim()) return;
  this.hasSentMessage = true;


  }

  @ViewChild('chatTextarea') chatTextarea!: ElementRef<HTMLTextAreaElement>;

autoResize(el: HTMLTextAreaElement, preferredScrollTop?: number): void {
  const MAX = 240;

  if (!el.value || el.value.trim().length === 0) {
    el.style.height = '';
    el.style.overflowY = 'hidden';
    el.scrollTop = 0;
    return;
  }

  const prevScrollTop = preferredScrollTop ?? el.scrollTop;

  const cs = window.getComputedStyle(el);
  const lineH = parseFloat(cs.lineHeight || '20') || 20;

  el.style.height = '';

  const scrollH = el.scrollHeight;
  const clientH = el.clientHeight || lineH;

  const fillRatio = scrollH / clientH;
  const shouldPreGrow = fillRatio >= 0.85;

  const desired = Math.min(scrollH + (shouldPreGrow ? lineH : 0), MAX);

  el.style.height = `${desired}px`;

  const capped = desired >= MAX;
  el.style.overflowY = capped ? 'auto' : 'hidden';

  // KEY FIX:
  // If we are composing at the top, NEVER snap to bottom.
  if (capped) {
    if (this.composingAtTop) {
      el.scrollTop = prevScrollTop; // preserve view near the top
      // If you want it ALWAYS pinned to the top, use this instead:
      // el.scrollTop = 0;
    } else {
      el.scrollTop = el.scrollHeight; // old behavior
    }
  }
}

private resetTextarea(): void {
  const el = this.chatTextarea?.nativeElement;
  if (!el) return;

  el.style.height = '';     
  el.style.overflowY = 'hidden';
  el.scrollTop = 0;
}



  recallPreviousMessage(): void {
    if (this.messageHistory.length === 0) return;
    if (this.historyIndex === -1) {
      this.historyIndex = this.messageHistory.length - 1;
    } else if (this.historyIndex > 0) {
      this.historyIndex--;
    }
    this.userInput = this.messageHistory[this.historyIndex];
  }

  recallNextMessage(): void {
    if (this.messageHistory.length === 0 || this.historyIndex === -1) return;
    if (this.historyIndex < this.messageHistory.length - 1) {
      this.historyIndex++;
      this.userInput = this.messageHistory[this.historyIndex];
    } else {
      this.historyIndex = -1;
      this.userInput = '';
    }
  }

  recallLastMessage(): void {
    if (this.messageHistory.length === 0) return;
    this.userInput = this.messageHistory[this.messageHistory.length - 1];
    this.historyIndex = this.messageHistory.length - 1;
  }

  get latestPromptText(): string {
    return this.messageHistory.length > 0
      ? this.messageHistory[this.messageHistory.length - 1]
      : '';
  }

  private resetUploadState(): void {
    this.selectedFile = undefined;
    this.selectedImagePreview = null;
  }

  convertMarkdown(markdown: unknown): string {
    const normalized = this.normalizeMessageContent(markdown);
    try {
      const aggressive = aggressivelyFormatMarkdownForCodeBlocks(normalized || '');
      return this.converter.makeHtml(aggressive);
    } catch {
      return normalized || '';
    }
  }

  getMessageDisplayContent(msg: Message): string {
    return this.normalizeMessageContent(msg?.content);
  }

  private normalizeMessageContent(content: unknown): string {
    if (typeof content === 'string') {
      const trimmed = content.trim();
      if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
        try {
          const parsed = JSON.parse(trimmed);
          const extracted = this.extractTextFromCohere(parsed);
          if (extracted !== null) return extracted;
        } catch {}
      }
      return content;
    }

    const extracted = this.extractTextFromCohere(content);
    if (extracted !== null) return extracted;

    return content == null ? '' : String(content);
  }

  private extractTextFromCohere(payload: unknown): string | null {
    if (!payload || typeof payload !== 'object') return null;
    const record = payload as Record<string, unknown>;
    if (typeof record.text === 'string') return record.text;

    const modelOutput = record.model_output;
    if (typeof modelOutput === 'string') return modelOutput;
    if (modelOutput && typeof modelOutput === 'object') {
      const nested = (modelOutput as Record<string, unknown>).text;
      if (typeof nested === 'string') return nested;
    }

    return null;
  }

  private highlightAll(): void {
    try {
      if (!this.prismLoaded || !this.prism) return;
      const root = this.messagesContainer?.nativeElement || document;
      if (this.prism.highlightAllUnder) {
        this.prism.highlightAllUnder(root);
      } else {
        this.prism.highlightAll();
      }
    } catch {}
  }

  scrollToBottom(): void {
    if (!this.messagesContainer?.nativeElement) return;
    const container = this.messagesContainer.nativeElement;
    container.scrollTop = container.scrollHeight;
    this.userScrolledUp = false;
    this.showScrollToBottom = false;
  }

  private scrollToBottomIfNeeded(): void {
    if (!this.messagesContainer?.nativeElement) return;
    if (!this.userScrolledUp) {
      setTimeout(() => {
        const el = this.messagesContainer.nativeElement;
        el.scrollTop = el.scrollHeight;
      }, 100);
    }
  }

  // ===================== Pinning =====================
  togglePinMessage(msg: Message): void {
    msg.pinned = !msg.pinned;
    this.pinnedMessages = this.currentMessages.filter(m => m.pinned);

    if (msg.pinned) {
      this.scrollToPinnedForId(msg.id || '');
    }
  }

  private scrollToPinnedForId(id: string): void {
    const container = this.messagesContainer?.nativeElement as HTMLElement;
    const el = document.getElementById(`msg-${id}`);
    if (!el || !container) return;

    const top = el.offsetTop - container.clientHeight / 2 + el.clientHeight / 2;
    container.scrollTo({ top, behavior: 'smooth' });

    el.classList.add('pinned-highlight');
    setTimeout(() => el.classList.remove('pinned-highlight'), 1000);
  }

  scrollToPinned(next: boolean): void {
    if (this.pinnedMessages.length === 0) return;

    if (next) {
      this.currentPinnedIndex = (this.currentPinnedIndex + 1) % this.pinnedMessages.length;
    } else {
      this.currentPinnedIndex =
        (this.currentPinnedIndex - 1 + this.pinnedMessages.length) %
        this.pinnedMessages.length;
    }

    const targetMessage = this.pinnedMessages[this.currentPinnedIndex];
    const element = document.getElementById(`msg-${targetMessage.id}`);

    if (element) {
      const container = this.messagesContainer?.nativeElement;
      const offsetTop = (element as any).offsetTop - (container as any).offsetTop - 20;
      container.scrollTo({
        top: offsetTop,
        behavior: 'smooth'
      });
    }
  }

  toggleUseWebSearch(): void {
    const ctl = this.form.get('useWebSearch');
    if (!ctl || ctl.disabled) {
      return;
    }

    const nextValue = !ctl.value;
    ctl.setValue(nextValue, { emitEvent: true });

    try {
      this.cdr.markForCheck();
    } catch {}
  }

  login() {
    this.isLoggedIn = true;
  }

  logout() {
    this.chatService.logout().subscribe({
      next: () => {
        this.isLoggedIn = false;
        this.form.get('useWebSearch')?.setValue(false);
      },
      error: () => {
        this.isLoggedIn = false;
        this.form.get('useWebSearch')?.setValue(false);
      }
    });
  }

  async saveMessageToNotes(msg: Message): Promise<void> {
    const normalizedContent = this.normalizeMessageContent(msg?.content);
    if (!normalizedContent.trim()) {
      return;
    }

    const who = msg.role === 'user' ? 'You' : msg.modelDisplayName || 'Assistant';

    const timestamp = msg.timestamp ? new Date(msg.timestamp as any).toLocaleString() : '';

    const titleBase = `Chat – ${who}`;
    const title = timestamp ? `${titleBase} (${timestamp})` : titleBase;

    await this.notesService.createNote({
      title,
      content: normalizedContent,
      labels: ['ClariChat'],
      color: '#fff8e1',
      pinned: false
    });
  }

  clearAllPins(ev?: MouseEvent) {
    ev?.stopPropagation();
    this.currentMessages.forEach(m => (m.pinned = false));
    this.pinnedMessages = [];
    this.showPinStack = false;
  }

// ───────── Action toast state ─────────
actionToastById: Record<
  string,
  { text: string; type: 'copied' | 'added' | 'warn' }
> = {};

private showActionToast(
  msg: Message,
  text: string,
  type: 'copied' | 'added' | 'warn' = 'copied'
): void {
  const id = msg.id;
  if (!id) return;

  this.actionToastById[id] = { text, type };

  setTimeout(() => {
    if (this.actionToastById[id]?.text === text) {
      delete this.actionToastById[id];
    }
  }, 3500);
}

  getTextareaPlaceholder(): string {

  // ✅ Special cases: image models get different placeholder
  if (this.selectedModel === 'stability' || this.selectedModel === 'dale' || this.selectedModel === 'grokimage') {
    return `Describe the image you want…`;
  }

  // chat placeholder - ONLY the AI name changes
  const modelName = this.selectedModelName || 'AI';
  const accessLabel = this.isLoggedIn ? 'Clarity Access' : 'Pro Access';

  return `Now using ${modelName} with ${accessLabel}`;
}

  /**
   * Load a conversation by ID (used when clicking from menu)
   */
  private async loadConversationById(conversationId: string): Promise<void> {
    try {
      const conversation = await firstValueFrom(this.chatService.loadConversationById(conversationId));
      if (conversation) {
        // Conversation is already set as current in the service
        // The subscription will handle updating currentMessages
        this.cdr.detectChanges();
      }
    } catch (err) {
      console.error('[ClariChat] Error loading conversation', err);
    }
  }

  //scroll rail (below)
  scrollToMessage(index: number): void {
  const el = this.msgElements?.[index];
  if (!el || !this.messagesContainer?.nativeElement) return;

  el.scrollIntoView({ behavior: 'smooth', block: 'center' });
}
  //scroll rail (above)



}